import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import type { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import '@wangeditor/editor/dist/css/style.css';
import { message } from 'antd';
import { uploadFile } from '@/utils/upload';
import styles from './index.module.less';

export interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  /** 编辑器最小高度（px） */
  minHeight?: number;
  /** 高度随内容自动增高（不固定高度，使用 minHeight） */
  autoHeight?: boolean;
  disabled?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  placeholder = '请输入内容',
  minHeight = 200,
  autoHeight = false,
  disabled = false,
}) => {
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const uploadFileRef = useRef(uploadFile);
  uploadFileRef.current = uploadFile;

  const toolbarConfig: Partial<IToolbarConfig> = useMemo(() => ({}), []);
  const editorConfig: Partial<IEditorConfig> = useMemo(
    () => ({
      placeholder,
      readOnly: disabled,
      scroll: !autoHeight,
      MENU_CONF: {
        uploadImage: {
          base64LimitSize: 0,
          async customUpload(file: File, insertFn: (url: string, alt: string, href: string) => void) {
            try {
              const urls = await uploadFileRef.current(file);
              const url = urls[0] ?? '';
              if (url) insertFn(url, file.name || '', url);
            } catch {
              message.error('图片上传失败');
            }
          },
        },
        uploadVideo: {
          async customUpload(file: File, insertFn: (url: string, poster?: string) => void) {
            try {
              const urls = await uploadFileRef.current(file);
              const url = urls[0] ?? '';
              if (url) insertFn(url);
            } catch {
              message.error('视频上传失败');
            }
          },
        },
      },
    }),
    [placeholder, disabled, autoHeight],
  );

  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  return (
    <div className={styles.richTextEditorWrap} style={{ minHeight: minHeight + 42 }}>
      {!disabled && (
        <Toolbar
          editor={editor}
          defaultConfig={toolbarConfig}
          mode="default"
          style={{ borderBottom: '1px solid #d9d9d9' }}
        />
      )}
      <Editor
        defaultConfig={editorConfig}
        value={value}
        onCreated={setEditor}
        onChange={(e) => onChange?.(e.getHtml())}
        mode="default"
        style={
          autoHeight
            ? { minHeight, height: 'auto', overflowY: 'visible' }
            : { height: minHeight, overflowY: 'auto' }
        }
      />
    </div>
  );
};

export default RichTextEditor;
